import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { ShapefileFileType } from "models/projects";

import { ColorPicker } from "components/base";
import { useStructuredNodeContext } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/StructuredNodeContext";
import { setInEditMode } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/reducer";
import {
  ActionButton,
  EditableLabel,
  FlexItem
} from "components/project/layers/components/layer-managers/shared";
import { useRightClickContextMenu } from "components/project/layers/components/layer-managers/shared/hooks/useRightClickContextMenu";
import { ShapefileActions } from "components/project/layers/components/layer-managers/shared/shapefile-actions/ShapefileActions";
import { ProjectTreeNodeT } from "components/project/layers/hooks";
import { SHAPEFILE_FOLDER_TYPE } from "components/project/layers/hooks/project-layer-tree/useProjectLayerTree.types";
import { useProjectDispatch } from "components/project/projects/context";
import { useCancelShapefileChanges } from "components/project/shapefiles/hooks";
import { useAcceptShapefileChanges } from "components/project/shapefiles/hooks/accept-shapefile-changes/useAcceptShapefileChanges";
import {
  useDeleteProjectShapefileMutation,
  useUpdateProjectShapefileMutation
} from "components/project/shapefiles/mutations";
import {
  INVALID_FILL_PATTERN_MESSAGE,
  INVALID_LINE_STYLE_MESSAGE,
  ShapefileStyleProp,
  isFillPattern,
  isLineStyle
} from "components/project/shapefiles/shapefiles.constants";

type OrgShapefileNodeProps = {
  node: ProjectTreeNodeT;
  setSelectedShapefile: (shapefile: ProjectTreeNodeT) => void;
};

export default function OrgShapefileNode(props: OrgShapefileNodeProps) {
  const { node, setSelectedShapefile } = props;

  const projectDispatch = useProjectDispatch();
  const { openContextMenu, contextMenuObject } = useRightClickContextMenu();

  const [{ inEditMode, originalTitle }, nodeDispatch] = useStructuredNodeContext();

  const role = useSelector((state: RootState) => state.auth.user.role);
  const isAdminOrPowerUser = role === "Admin" || role === "PowerUser";

  const deleteShapefile = useDeleteProjectShapefileMutation({
    projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION
  });

  const updateShapefileMutation = useUpdateProjectShapefileMutation({
    projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION
  });

  const acceptChanges = useAcceptShapefileChanges({
    node,
    projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION
  });

  const cancelChanges = useCancelShapefileChanges();

  const handleChange = (key: string, value: string) => {
    if (key === ShapefileStyleProp.LineStyle && !isLineStyle(value)) {
      toast.error(INVALID_LINE_STYLE_MESSAGE);
      return;
    }

    if (key === ShapefileStyleProp.FillPattern && !isFillPattern(value)) {
      toast.error(INVALID_FILL_PATTERN_MESSAGE);
      return;
    }

    updateShapefileMutation.mutate({
      body: {
        key,
        value
      },
      projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION,
      shapefileId: node.shapefileId
    });
  };

  return (
    <LeafTitle onContextMenu={openContextMenu}>
      <FlexItem>
        {!inEditMode && (
          <ColorPicker
            isAtShapefileLevel={true}
            onChange={handleChange}
            onChangeLineStyle={handleChange}
            onChangeFillPattern={handleChange}
            values={{
              color: node.color,
              strokeColor: node.strokeColor,
              thickness: node.thickness,
              lineStyle:
                node.fileType !== ShapefileFileType.GeoTiff
                  ? node.lineStyle ?? "solid"
                  : undefined,
              fillPattern:
                node.fileType !== ShapefileFileType.GeoTiff
                  ? node.fillPattern ?? "none"
                  : undefined,
              opacity: (node.opacity ?? 1) * 100
            }}
          />
        )}
        <EditableLabel
          isLoading={acceptChanges.isLoading}
          onCancel={cancelChanges.mutate}
          onChange={acceptChanges.mutate}
        />
      </FlexItem>
      {!inEditMode && (
        <FlexItem>
          <ShapefileActions
            isDisabled={!isAdminOrPowerUser}
            hasSelectItems={false} // Only available in project tree
            contextMenu={contextMenuObject}
            setIsRenaming={() =>
              setInEditMode(nodeDispatch, true, (isDraggable) =>
                projectDispatch({ payload: { isTreeDraggable: isDraggable } })
              )
            }
            viewShapefileDetails={() => setSelectedShapefile(node)}
            fileName={originalTitle || node.title}
            deleteShapefile={() => {
              deleteShapefile.mutate({ shapefileId: node.shapefileId });
            }}
          />
        </FlexItem>
      )}
    </LeafTitle>
  );
}

const LeafTitle = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  justify-content: space-between;

  .drag-container {
    position: absolute;
    opacity: 0.5;
    left: -20px;
    top: 4px;
    color: #a2aaad;
    cursor: move;
  }

  ${ActionButton} {
    svg {
      opacity: 0;
    }
  }

  &:hover,
  &.in-edit-mode {
    svg {
      opacity: 1;
    }
  }

  input[type="text"] {
    height: 24px;
    outline: none;
  }

  input[type="text"]:focus {
    outline: none 0;
  }

  label {
    margin-left: 5px !important;
  }
`;
